import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, FileText, Download, Briefcase, Gavel, ScrollText, ShieldCheck, FileSignature, Bell, Scale } from "lucide-react";
import { Field, Select, Textarea, GoldButton, GhostButton, PageHeader } from "@/components/Ui";
import { caseLabel, clientLabel } from "@/lib/labels";

const TEMPLATE_ICON = {
    plaint: Briefcase,
    reply: ScrollText,
    affidavit: FileSignature,
    vakalatnama: FileText,
    demand_notice: Bell,
    legal_opinion: ShieldCheck,
    bail_application: Scale,
    adjournment: Gavel,
};

export default function AIDocs() {
    const [params] = useSearchParams();
    const [templates, setTemplates] = useState([]);
    const [clients, setClients] = useState([]);
    const [cases, setCases] = useState([]);
    const [picked, setPicked] = useState(params.get("template") || "plaint");
    const [clientId, setClientId] = useState("");
    const [caseId, setCaseId] = useState(params.get("case_id") || "");
    const [instructions, setInstructions] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [refineOpen, setRefineOpen] = useState(false);
    const [refineText, setRefineText] = useState("");
    const [previousOutput, setPreviousOutput] = useState("");

    useEffect(() => {
        api.get("/ai-docs/templates").then(r => setTemplates(r.data || [])).catch(e => toast.error(apiError(e)));
        api.get("/clients").then(r => setClients(r.data || [])).catch(() => {});
        api.get("/cases").then(r => {
            const list = r.data || [];
            setCases(list);
            // auto-fill client when case_id is provided via querystring
            const initial = params.get("case_id");
            if (initial) {
                const c = list.find(x => x.id === initial);
                if (c?.client_id) setClientId(c.client_id);
            }
        }).catch(() => {});
    }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    const filteredCases = useMemo(() => {
        if (!clientId) return cases;
        return cases.filter(c => c.client_id === clientId);
    }, [cases, clientId]);

    const onPickClient = (cid) => {
        const caseOk = !caseId || cases.find(c => c.id === caseId)?.client_id === cid;
        setClientId(cid); if (!caseOk) setCaseId("");
    };
    const onPickCase = (cid) => {
        setCaseId(cid);
        const c = cases.find(x => x.id === cid);
        if (c?.client_id) setClientId(c.client_id);
    };

    const generate = async () => {
        if (!picked) { toast.error("Pick a template"); return; }
        if (!caseId && !clientId && !instructions.trim()) {
            toast.warning("Tip: pick a case or client (or add custom instructions) for context-aware drafts. Continuing with a generic template.");
        }
        setBusy(true);
        setResult(null);
        try {
            const payload = { template: picked, formats: ["docx", "pdf"] };
            if (caseId) payload.case_id = caseId;
            if (clientId) payload.client_id = clientId;
            if (instructions.trim()) payload.custom_instructions = instructions.trim();
            const { data } = await api.post("/ai-docs/generate", payload);
            setResult(data);
            setPreviousOutput(data.preview || "");
            setRefineOpen(false); setRefineText("");
            toast.success(`${data.title} generated · ${data.documents.length} file${data.documents.length > 1 ? "s" : ""} saved to Documents`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const refine = async () => {
        if (!refineText.trim()) { toast.error("Tell the AI what to change first"); return; }
        if (!result) return;
        setBusy(true);
        try {
            const payload = {
                template: picked, formats: ["docx", "pdf"],
                previous_output: previousOutput,
                refinement_instructions: refineText.trim(),
            };
            if (caseId) payload.case_id = caseId;
            if (clientId) payload.client_id = clientId;
            if (instructions.trim()) payload.custom_instructions = instructions.trim();
            const { data } = await api.post("/ai-docs/generate", payload);
            setResult(data);
            setPreviousOutput(data.preview || "");
            toast.success("Refined · new version saved to Documents");
            setRefineOpen(false); setRefineText("");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const download = async (doc) => {
        try {
            const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            // If the server returned a JSON error wrapped in a blob, surface it as a proper error
            if (res.data?.type && res.data.type.includes("application/json")) {
                const text = await res.data.text();
                let detail = text;
                try { detail = JSON.parse(text).detail || text; } catch {}
                toast.error(detail || "Could not download document");
                return;
            }
            const blob = new Blob([res.data], { type: doc.mime });
            const a = document.createElement("a");
            const url = URL.createObjectURL(blob);
            a.href = url;
            a.download = doc.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(apiError(e)); }
    };

    const activeTpl = templates.find(t => t.key === picked);

    return (
        <>
            <PageHeader
                title="AI Document Studio"
                subtitle="Generate court-ready drafts in Word + PDF. Pick a template, link a case, add your instructions — Law OS AI fills the prose and the file lands in Documents, mapped to the litigation and client."
            />

            <div className="grid lg:grid-cols-12 gap-6">
                {/* Template chooser */}
                <div className="lg:col-span-4 bg-muted border border-border p-5">
                    <div className="overline mb-3">Templates</div>
                    <div className="space-y-2" data-testid="ai-docs-templates">
                        {templates.map(t => {
                            const Icon = TEMPLATE_ICON[t.key] || FileText;
                            const active = picked === t.key;
                            return (
                                <button key={t.key} onClick={() => setPicked(t.key)} data-testid={`ai-docs-tpl-${t.key}`}
                                    className={`w-full text-left p-3 border transition-colors flex items-start gap-3 ${active ? "border-primary bg-background" : "border-border hover:border-primary/60"}`}>
                                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5}/>
                                    <div>
                                        <div className={`text-sm ${active ? "text-primary" : "text-foreground"}`}>{t.title}</div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">{t.sections.length} sections</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Right pane */}
                <div className="lg:col-span-8 space-y-6">
                    {activeTpl && (
                        <div className="bg-muted border border-border p-5">
                            <div className="overline mb-2">Outline · {activeTpl.title}</div>
                            <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                                {activeTpl.sections.map((s, i) => <li key={`${i}-${s.slice(0,40)}`} className="leading-relaxed">{s}</li>)}
                            </ol>
                        </div>
                    )}

                    <div className="bg-muted border border-border p-5 space-y-4">
                        <div className="overline">Context</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Client (optional)">
                                <Select value={clientId} onChange={e => onPickClient(e.target.value)} data-testid="ai-docs-client-select"
                                    options={[{ value: "", label: "— none —" }, ...clients.map(c => ({ value: c.id, label: clientLabel(c) }))]}/>
                            </Field>
                            <Field label="Case (recommended)" hint={clientId ? "Only this client's cases." : "Pick a case to auto-fill the client."}>
                                <Select value={caseId} onChange={e => onPickCase(e.target.value)} data-testid="ai-docs-case-select"
                                    options={[{ value: "", label: "— none —" }, ...filteredCases.map(c => ({ value: c.id, label: caseLabel(c) }))]}/>
                            </Field>
                        </div>
                        <Field label="Custom instructions for AI (optional)" hint="Mention any specifics — facts, names, dates, statutes you want emphasised.">
                            <Textarea rows="3" value={instructions} onChange={e => setInstructions(e.target.value)} data-testid="ai-docs-instructions"
                                placeholder="e.g. Emphasise that the cheque was issued on 12 Jan 2026 and dishonoured on 18 Jan 2026 with reason 'funds insufficient'."/>
                        </Field>
                        <div className="flex justify-between items-center">
                            <div className="text-[10px] text-muted-foreground">Indian-court drafts · Claude Sonnet 4.5 · usually takes 15-45s.</div>
                            <GoldButton onClick={generate} disabled={busy} data-testid="ai-docs-generate-btn">
                                <Sparkles className="w-4 h-4 inline mr-2" strokeWidth={2}/>
                                {busy ? "Drafting…" : "Generate document"}
                            </GoldButton>
                        </div>
                    </div>

                    {result && (
                        <div className="bg-muted border border-primary/40 p-5 space-y-4" data-testid="ai-docs-result">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="overline text-primary">Drafted</div>
                                    <div className="font-serif text-xl mt-1">{result.title}</div>
                                </div>
                                <div className="flex gap-2 flex-wrap items-center">
                                    <GhostButton onClick={() => setRefineOpen(o => !o)} data-testid="ai-docs-refine-btn">
                                        <Sparkles className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={1.5}/>
                                        {refineOpen ? "Cancel refine" : "Refine this draft"}
                                    </GhostButton>
                                    {result.documents.map(d => (
                                        <GoldButton key={d.id} onClick={() => download(d)} data-testid={`ai-docs-download-${d.format}`}>
                                            <Download className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2}/>
                                            {d.format.toUpperCase()}
                                        </GoldButton>
                                    ))}
                                </div>
                            </div>
                            {refineOpen && (
                                <div className="bg-background border border-primary/30 p-4 space-y-3" data-testid="ai-docs-refine-panel">
                                    <div className="overline text-primary">Refine</div>
                                    <p className="text-[10px] text-muted-foreground">Tell the AI what to change. The prior draft is kept as context so structure stays consistent.</p>
                                    <Textarea rows="3" value={refineText} onChange={e => setRefineText(e.target.value)} data-testid="ai-docs-refine-input"
                                        placeholder="e.g. Make the tone more assertive · add a paragraph about the 30-day cure period · cite Section 138 NI Act prominently"/>
                                    <div className="flex justify-end">
                                        <GoldButton onClick={refine} disabled={busy} data-testid="ai-docs-refine-submit">
                                            <Sparkles className="w-4 h-4 inline mr-1.5" strokeWidth={2}/>
                                            {busy ? "Refining…" : "Apply refinement"}
                                        </GoldButton>
                                    </div>
                                </div>
                            )}
                            <div className="bg-background border border-border p-4 max-h-[420px] overflow-y-auto">
                                <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono leading-relaxed">{result.preview}</pre>
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                                Both files have been auto-saved to Documents (mapped to the litigation + client). You can re-download or re-share them from there at any time.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
